import { RoleFunction } from '@eagle/common';
import { Account, AccountType, DomainProtectedConfigResponse } from '@eagle/core-data-types';
import { Card, styled, TableBody, TableCell, TableHead, TableRow, Typography } from '@ethings/ui-toolkit';
import axiosStatic from 'axios';
import { FC, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { AppliedFilter, AppliedFilterType, BoldMatchedText, FetchOneOfAll, FilterDataTypes, FilterFieldNewProps, FilterTypes, FlexBox, getShowDeleted, handleSetShowDeleted, Icon, NewFilter, Pagination, ResultsDetailCard, ResultsTableRow, SAVED_FILTER_KEY, SearchResultsTableView, useBoolFlag } from '../../components';
import { T_MANY, T_ONE } from '../../constants';
import { useFetchAllCache, usePromise, useSmallScreen } from '../../hooks';
import { CacheDataTypes, PageAction, Undefinable } from '../../types';
import { filterToQuery, getListResultDescription, includeDeletedFilters, loadConfig, testid, trackEvent, useHasAuthorization } from '../../util';
import { FindItemsDeferredResult, ListPage, ListPageQuery } from '../list';
import { AccountRowButton } from './account-row-button';

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableCell = styled(TableCell)();

const ACCOUNT_CREATE_ROLE = [RoleFunction.ACCOUNT_CREATOR] as const;
const SHOW_DELETED_STORAGE_KEY = 'admin-accounts';

export const AccountsList: FC = () => {
  const { axios, restClient, userInfo } = useAuthenticated();
  const smallScreen = useSmallScreen();
  const { t } = useTranslation(['common']);
  const itemType = t('common:terms.account', { count: T_MANY });
  const navigate = useNavigate();
  const enableFilters = useBoolFlag('portals-accounts-list-filtering-feature');
  const [showDeleted, setShowDeleted] = useState(getShowDeleted(SHOW_DELETED_STORAGE_KEY, enableFilters));
  const accountTypesCache = useFetchAllCache(CacheDataTypes.MY_ACCESSIBLE_ACCOUNT_TYPE);
  const href = useHref('/account');
  const { hasAuthorization } = useHasAuthorization();
  const showCreateButton = useBoolFlag('manage-account-create-feature') && hasAuthorization(ACCOUNT_CREATE_ROLE);
  const [domainConfigs, setDomainConfigs] = useState<Record<string, Partial<DomainProtectedConfigResponse>>>({});

  const findAccounts = useCallback(({ filters, pagination, search, showDeleted }: ListPageQuery): FindItemsDeferredResult<Account> => {
    const cancelToken = axiosStatic.CancelToken.source();

    return {
      cancel: cancelToken.cancel,
      promise: restClient.account.getAllV1Paged({
        ...pagination,
        ...(search ? { search, searchFields: ['display'] } : {}),
        filter: { _id: { $ne: userInfo.accountId }, ...filterToQuery(filters), ...includeDeletedFilters(showDeleted) },
        sort: !search ? JSON.stringify({ display: 'asc' }) : undefined,
      }, { cancelToken: cancelToken.token }).then((response) => {
        const matchCount = response.count;

        const resultDescription = getListResultDescription({ count: matchCount, entityKey: 'common:terms.account', search, t });

        if (search) {
          trackEvent('keyword_search', 'returned_results', 'account_list', { 'results_type': 'account', 'results_count': matchCount, 'search_term': search });
        }

        const homeDomains = [...response.entities.reduce((set, account) => set.add(account.homeDomain), new Set<string>())];

        Promise.all(homeDomains.map((homeDomain) => loadConfig(axios, homeDomain))).then((domains) => {
          setDomainConfigs(homeDomains.reduce((obj, homeDomain, index) => ({ ...obj, [homeDomain]: domains[index] }), {}));
        }).catch(() => { });

        return {
          result: {
            itemCount: matchCount,
            results: response.entities,
          },
          resultDescription,
        };
      }),
    };
  }, [restClient, t, userInfo.accountId, axios]);

  const [myAccounts] = usePromise(async () => {
    return restClient.my.accounts.getAll();
  }, [restClient]);

  const navigateToAccount = (account: Account): void => {
    navigate(`${href}/${encodeURIComponent(account._id)}`);
  };

  const renderTableRowContent = (item: Account): JSX.Element => {
    const showSwitchAccount = !item.deleted && !!myAccounts?.some((account) => account._id === item._id);
    const productName = domainConfigs[item.homeDomain]?.productName;

    if (smallScreen) {
      return <FlexBox sx={{ width: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'><BoldMatchedText text={item.display} /></Typography>
        {showSwitchAccount && <AccountRowButton accountId={item._id} />}
      </FlexBox>;
    }
    return <>
      <StyledTableCell component="div">
        <Typography><BoldMatchedText text={item.display} /></Typography>
      </StyledTableCell>
      <StyledTableCell component="div">
        {item.accountTypeId
          ? <FetchOneOfAll
            dataType={CacheDataTypes.ACCOUNT_TYPE}
            id={item.accountTypeId}
            renderFactory={(accountType: AccountType) => <Typography><BoldMatchedText text={accountType.display} /></Typography>}
          />
          : <Typography color="text.secondary" fontStyle="italic" variant="body2">
            {t('common:page.accounts-list.no-account-type.labels')}
          </Typography>
        }
      </StyledTableCell>
      <StyledTableCell component="div">
        <Typography><BoldMatchedText text={item.homeDomain} /></Typography>
        {productName
          ? <Typography><BoldMatchedText text={productName} /></Typography>
          : <Typography color="text.secondary" fontStyle="italic" variant="body2">
            {t('common:page.accounts-list.no-product-name.labels')}
          </Typography>
        }
      </StyledTableCell>
      <StyledTableCell component="div" sx={{ width: 200 }}>
        {showSwitchAccount && <AccountRowButton accountId={item._id} />}
      </StyledTableCell>
    </>;
  };

  const renderDesktopContent = (isLoading: boolean, items: Account[]): JSX.Element => <>
    <TableHead component="div">
      <TableRow component="div">
        <StyledTableCell component="div">{t('common:terms.name')}</StyledTableCell>
        <StyledTableCell component="div">{t('common:page.accounts-list.account-type.labels')}</StyledTableCell>
        <StyledTableCell component="div">{t('common:page.accounts-list.home-domain.labels')}</StyledTableCell>
        <StyledTableCell component="div"></StyledTableCell>
      </TableRow>
    </TableHead>
    <StyledTableBody isLoading={isLoading} component="div">
      {items.map((item, i) => (
        <ResultsTableRow
          data-testid={testid`account-${item._id}`}
          key={i}
          deleted={item.deleted !== null}
          content={renderTableRowContent(item)}
          href={`${href}/${item._id}`}
        />
      ))}
    </StyledTableBody>
  </>;

  const renderMobileContent = (_displayOnCard: boolean, isLoading: boolean, items: Account[]): JSX.Element => <>
    {items.map((item, i) => {
      return (
        <Card key={i} style={{ filter: isLoading ? 'blur(1px)' : '', marginTop: '8px', marginBottom: '8px', overflow: 'visible', width: '100%' }}>
          <ResultsDetailCard
            content={renderTableRowContent(item)}
            href={`${href}/${item._id}`}
            sx={{ p: 2 }}
            deleted={item.deleted !== null}
            data-testid={testid`account-${item._id}`}
          />
        </Card>
      );
    })}
  </>;

  const renderTableContent = (
    isLoading: boolean,
    items: Undefinable<Account[]>,
    matchCount: number,
    pagination: Pagination,
    setPagination: (value: Pagination) => void,
    text: Undefinable<string>,
  ): JSX.Element => {
    return (
      <SearchResultsTableView<Account>
        isLoading={isLoading}
        items={items}
        matchCount={matchCount}
        noResultsInstructions={t('common:common.hint.list.none', { entity: itemType })}
        pagination={pagination}
        renderDesktopContent={(isLoading, items) => renderDesktopContent(isLoading, items)}
        renderMobileContent={(displayOnCard, isLoading, items) => renderMobileContent(displayOnCard, isLoading, items)}
        setPagination={setPagination}
        text={text}
      />
    );
  };

  const actions: PageAction[] = [];

  if (showCreateButton) {
    actions.push({
      href: `${href}/create`,
      icon: <Icon icon="AccountIcon" />,
      label: t('common:common.action.create-entity', { entity: t('common:terms.account', { count: T_ONE }) }),
      onClick: () => navigate(`${href}/create`),
      testId: 'create-account-button',
    });
  }

  const renderFilterContent = (
    filters: AppliedFilter<AppliedFilterType>[],
    setFilterOpen: (value: boolean) => void,
    onFiltersChanged: (filters: AppliedFilter<AppliedFilterType>[]) => unknown,
  ): JSX.Element => {
    const filterFields: FilterFieldNewProps[] = [
      {
        apiUrl: '/api/v1/account',
        attributes: {
          typeCache: accountTypesCache,
          typePath: FilterTypes.ACCOUNT_TYPE,
        },
        dataType: FilterDataTypes.API,
        fieldLabel: t('common:component.filter.labels.select-an-account'),
        propertyLabel: t('common:terms.account', { count: T_ONE }),
        typePropertyName: FilterTypes.ID,
      },
    ];

    return (
      <NewFilter
        data-testid="accounts-filter"
        canShowDeleted
        filterFields={filterFields}
        filters={filters}
        includeDeleted={showDeleted}
        onCloseClicked={() => setFilterOpen(false)}
        onFiltersChanged={onFiltersChanged}
        savedFilterKey={SAVED_FILTER_KEY}
        setIncludeDeleted={(value) => handleSetShowDeleted(value, setShowDeleted, SHOW_DELETED_STORAGE_KEY)}
        storageKey="accounts"
      />
    );
  };

  return (
    <ListPage<Account>
      id='account'
      data-testid='account-list'
      actions={actions}
      filterFields={[]}
      icon={<Icon icon="AccountIcon" />}
      navigateToEntity={navigateToAccount}
      onQueryChanged={findAccounts}
      pagination={{ limit: 50, skip: 0 }}
      renderFilterContent={renderFilterContent}
      renderTableContent={renderTableContent}
      showFilterButton={!!enableFilters}
      storageKey="accounts"
      prePopulateSearch={true}
      title={t('common:terms.account', { count: T_MANY })}
    />
  );
};
